import type { ContactMultiValueItemResponseDto } from "@shared/generated/crm";

export const pickFirstMultiValue = (
  values: ContactMultiValueItemResponseDto[] | null,
): string | null => {
  if (!values || values.length === 0) return null;
  const [first] = [...values].sort((a, b) => a.order - b.order);
  return first?.value.trim() || null;
};

type SocialHandleParseResult =
  | { ok: true; handle: string }
  | { ok: false; error: string };

function toParsedUrl(raw: string): URL | null {
  const withProtocol = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  try {
    return new URL(withProtocol);
  } catch {
    return null;
  }
}

function matchesDomain(hostname: string, domain: string): boolean {
  const normalizedHostname = hostname.replace(/^www\./i, "");
  return (
    normalizedHostname === domain || normalizedHostname.endsWith(`.${domain}`)
  );
}

function looksLikeUrl(raw: string): boolean {
  const value = raw.trim();
  if (/^https?:\/\//i.test(value) || /^www\./i.test(value)) return true;
  return /(?:^|\.)(?:instagram|linkedin|facebook)\.com\b/i.test(value);
}

function stripHandleDecorators(raw: string): string {
  return raw.trim().replace(/^@/, "");
}

const isValidInstagramHandle = (handle: string): boolean => {
  if (handle.length < 1 || handle.length > 30) return false;
  if (!/^[a-zA-Z0-9._]+$/.test(handle)) return false;
  if (handle.startsWith(".") || handle.endsWith(".")) return false;
  if (handle.includes("..")) return false;
  return true;
};

// LinkedIn vanity URLs: 3–100 chars, letters/numbers/hyphens (no spaces or other symbols).
const isValidLinkedInHandle = (handle: string): boolean =>
  /^[a-zA-Z0-9-]{3,100}$/.test(handle);

// Facebook only enforces a 5-char minimum on newly created usernames — plenty
// of existing profiles (e.g. facebook.com/zuck) predate that rule, so we only
// validate the character set here, not a minimum length. A username can start
// with a digit (e.g. facebook.com/9gag) but must contain at least one letter,
// which is what distinguishes it from a numeric profile id.
const isValidFacebookHandle = (handle: string): boolean => {
  if (handle.length < 1 || handle.length > 50) return false;
  if (!/^[a-zA-Z0-9][a-zA-Z0-9.]*$/.test(handle)) return false;
  if (!/[a-zA-Z]/.test(handle)) return false;
  if (handle.includes("..") || handle.endsWith(".")) return false;
  return true;
};

// Profiles without a vanity username use facebook.com/profile.php?id=<numeric
// id> instead — the id lives in the query string, not the path, so it needs
// its own extraction/validation rather than being treated as a path segment.
const FACEBOOK_PROFILE_ID_PATH = "profile.php";

const isValidFacebookProfileId = (id: string): boolean => /^\d+$/.test(id);

// Contacts can end up with more than one link for the same platform (e.g. a
// stale CRM-synced value plus a manually-added one). We don't enforce
// uniqueness on the links array, so when there are multiple matches, the most
// recently added one (highest order) wins.
type MatchedPath = { path: string; search: string };

function extractMostRecentMatchingPath(
  links: ContactMultiValueItemResponseDto[] | null,
  domain: string,
): MatchedPath | null {
  if (!links || links.length === 0) return null;
  const sorted = [...links].sort((a, b) => a.order - b.order);
  let mostRecentMatch: MatchedPath | null = null;
  for (const item of sorted) {
    const parsed = toParsedUrl(item.value.trim());
    if (parsed && matchesDomain(parsed.hostname, domain)) {
      mostRecentMatch = {
        path: parsed.pathname.replace(/^\/|\/$/g, ""),
        search: parsed.search,
      };
    }
  }
  return mostRecentMatch;
}

// Path segments from URL.pathname stay percent-encoded (e.g. "jos%C3%A9" for
// "josé"); decode before validating so we're checking the actual characters.
function decodeHandleSegment(raw: string): string | null {
  try {
    return decodeURIComponent(raw);
  } catch {
    return null;
  }
}

export const getInstagramHandle = (
  links: ContactMultiValueItemResponseDto[] | null,
): string | null => {
  const match = extractMostRecentMatchingPath(links, "instagram.com");
  if (!match || !match.path || match.path.includes("/")) return null;
  const handle = decodeHandleSegment(match.path);
  return handle && isValidInstagramHandle(handle) ? handle : null;
};

export const getLinkedInHandle = (
  links: ContactMultiValueItemResponseDto[] | null,
): string | null => {
  const match = extractMostRecentMatchingPath(links, "linkedin.com");
  if (!match) return null;
  const result = /^in\/([^/]+)$/i.exec(match.path);
  if (!result) return null;
  const handle = decodeHandleSegment(result[1]);
  return handle && isValidLinkedInHandle(handle) ? handle : null;
};

export const getFacebookHandle = (
  links: ContactMultiValueItemResponseDto[] | null,
): string | null => {
  const match = extractMostRecentMatchingPath(links, "facebook.com");
  if (!match) return null;

  if (match.path === FACEBOOK_PROFILE_ID_PATH) {
    const id = new URLSearchParams(match.search).get("id");
    return id && isValidFacebookProfileId(id) ? id : null;
  }

  if (!match.path || match.path.includes("/")) return null;
  const handle = decodeHandleSegment(match.path);
  return handle && isValidFacebookHandle(handle) ? handle : null;
};

export const buildInstagramLink = (handle: string): string =>
  `https://www.instagram.com/${stripHandleDecorators(handle)}`;

export const buildLinkedInLink = (handle: string): string =>
  `https://www.linkedin.com/in/${stripHandleDecorators(handle)}`;

export const buildFacebookLink = (handle: string): string => {
  const stripped = stripHandleDecorators(handle);
  return isValidFacebookProfileId(stripped)
    ? `https://www.facebook.com/${FACEBOOK_PROFILE_ID_PATH}?id=${stripped}`
    : `https://www.facebook.com/${stripped}`;
};

export const parseInstagramHandleInput = (
  raw: string,
): SocialHandleParseResult => {
  const trimmed = raw.trim();
  if (!trimmed) return { ok: true, handle: "" };

  if (looksLikeUrl(trimmed)) {
    const parsed = toParsedUrl(trimmed);
    if (!parsed || !matchesDomain(parsed.hostname, "instagram.com")) {
      return {
        ok: false,
        error: "Enter a valid Instagram profile URL",
      };
    }
    const path = parsed.pathname.replace(/^\/|\/$/g, "");
    if (!path || path.includes("/") || !isValidInstagramHandle(path)) {
      return {
        ok: false,
        error: "Enter a valid Instagram profile URL",
      };
    }
    return { ok: true, handle: path };
  }

  const handle = stripHandleDecorators(trimmed);
  if (!isValidInstagramHandle(handle)) {
    return {
      ok: false,
      error:
        "Enter a valid Instagram username (1–30 letters, numbers, periods, or underscores)",
    };
  }
  return { ok: true, handle };
};

export const parseLinkedInHandleInput = (
  raw: string,
): SocialHandleParseResult => {
  const trimmed = raw.trim();
  if (!trimmed) return { ok: true, handle: "" };

  if (looksLikeUrl(trimmed)) {
    const parsed = toParsedUrl(trimmed);
    if (!parsed || !matchesDomain(parsed.hostname, "linkedin.com")) {
      return {
        ok: false,
        error: "Enter a valid LinkedIn profile URL",
      };
    }
    const path = parsed.pathname.replace(/^\/|\/$/g, "");
    const match = /^in\/([^/]+)$/i.exec(path);
    const handle = match?.[1];
    if (!handle || !isValidLinkedInHandle(handle)) {
      return {
        ok: false,
        error: "Enter a valid LinkedIn profile URL (linkedin.com/in/...)",
      };
    }
    return { ok: true, handle };
  }

  const handle = stripHandleDecorators(trimmed);
  if (!isValidLinkedInHandle(handle)) {
    return {
      ok: false,
      error:
        "Enter a valid LinkedIn username (3–100 letters, numbers, or hyphens)",
    };
  }
  return { ok: true, handle };
};

export const parseFacebookHandleInput = (
  raw: string,
): SocialHandleParseResult => {
  const trimmed = raw.trim();
  if (!trimmed) return { ok: true, handle: "" };

  if (looksLikeUrl(trimmed)) {
    const parsed = toParsedUrl(trimmed);
    if (!parsed || !matchesDomain(parsed.hostname, "facebook.com")) {
      return {
        ok: false,
        error: "Enter a valid Facebook profile URL",
      };
    }
    const path = parsed.pathname.replace(/^\/|\/$/g, "");

    if (path === FACEBOOK_PROFILE_ID_PATH) {
      const id = parsed.searchParams.get("id");
      if (!id || !isValidFacebookProfileId(id)) {
        return {
          ok: false,
          error: "Enter a valid Facebook profile URL",
        };
      }
      return { ok: true, handle: id };
    }

    if (!path || path.includes("/") || !isValidFacebookHandle(path)) {
      return {
        ok: false,
        error: "Enter a valid Facebook profile URL",
      };
    }
    return { ok: true, handle: path };
  }

  const handle = stripHandleDecorators(trimmed);
  if (!isValidFacebookProfileId(handle) && !isValidFacebookHandle(handle)) {
    return {
      ok: false,
      error:
        "Enter a valid Facebook username (letters, numbers, or periods, starting with a letter)",
    };
  }
  return { ok: true, handle };
};

export type SocialHandleFieldId =
  | "instagramHandle"
  | "linkedinHandle"
  | "facebookHandle";

export const isSocialHandleFieldId = (id: string): id is SocialHandleFieldId =>
  id === "instagramHandle" ||
  id === "linkedinHandle" ||
  id === "facebookHandle";

type SocialHandleFieldErrors = Partial<Record<SocialHandleFieldId, string>>;

export const validateSocialHandleFields = (
  values: Record<string, string>,
): SocialHandleFieldErrors => {
  const errors: SocialHandleFieldErrors = {};

  const instagramResult = parseInstagramHandleInput(
    values.instagramHandle ?? "",
  );
  if (!instagramResult.ok) {
    errors.instagramHandle = instagramResult.error;
  }

  const linkedinResult = parseLinkedInHandleInput(values.linkedinHandle ?? "");
  if (!linkedinResult.ok) {
    errors.linkedinHandle = linkedinResult.error;
  }

  const facebookResult = parseFacebookHandleInput(values.facebookHandle ?? "");
  if (!facebookResult.ok) {
    errors.facebookHandle = facebookResult.error;
  }

  return errors;
};

export const normalizeSocialHandleFields = (
  values: Record<string, string>,
): { instagramHandle: string; linkedinHandle: string; facebookHandle: string } => {
  const instagramResult = parseInstagramHandleInput(
    values.instagramHandle ?? "",
  );
  const linkedinResult = parseLinkedInHandleInput(values.linkedinHandle ?? "");
  const facebookResult = parseFacebookHandleInput(values.facebookHandle ?? "");

  return {
    instagramHandle: instagramResult.ok
      ? instagramResult.handle
      : (values.instagramHandle ?? "").trim(),
    linkedinHandle: linkedinResult.ok
      ? linkedinResult.handle
      : (values.linkedinHandle ?? "").trim(),
    facebookHandle: facebookResult.ok
      ? facebookResult.handle
      : (values.facebookHandle ?? "").trim(),
  };
};
